import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { supabase } from '@/lib/supabase'
import { TV_DATA_QUERY_KEY } from '@/hooks/useTvData'
import { APP_SETTINGS_QUERY_KEY, APP_SETTINGS_TV_LISTINGS_DAYS_KEY } from '@/config/appSettingsKeys'
import { computeNextRunAt, FREEVIEW_EPG_FEED_NAME } from '@/lib/listingsSyncUtils'
import { AdminSectionCard } from '@/components/listings-management/AdminSectionCard'

interface SyncState {
  last_success_at: string | null
  last_status: string | null
  last_error: string | null
  bytes: number | null
  min_programme_date: string | null
  max_programme_date: string | null
}

interface SyncRun {
  id: string
  started_at: string
  finished_at: string | null
  trigger_type: string
  status: string
  error: string | null
}

interface FeedSchedule {
  id: string
  enabled: boolean
  timezone: string
  weekday: number
  time_of_day: string
}

interface EpgSettingsSectionProps {
  userId: string
  feedId: string | undefined
}

export function EpgSettingsSection({ userId, feedId }: EpgSettingsSectionProps) {
  const queryClient = useQueryClient()
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [scheduleWeekday, setScheduleWeekday] = useState(0)
  const [scheduleTime, setScheduleTime] = useState('02:00')
  const [scheduleTimezone, setScheduleTimezone] = useState('Europe/London')
  const [clearExpiredMessage, setClearExpiredMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  )
  const [clearListingsMessage, setClearListingsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  )
  const [rebuildMessage, setRebuildMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [pruneArchivesMessage, setPruneArchivesMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  )
  const [durationMessage, setDurationMessage] = useState<string | null>(null)

  const { data: syncState } = useQuery({
    queryKey: ['external-feed-sync-state', feedId],
    queryFn: async () => {
      if (!feedId) return null
      const { data, error } = await supabase.from('external_feed_sync_state').select('*').eq('feed_id', feedId).single()
      if (error) throw error
      return data as SyncState
    },
    enabled: !!feedId,
  })

  const { data: runs = [] } = useQuery({
    queryKey: ['external-feed-sync-runs', feedId],
    queryFn: async () => {
      if (!feedId) return []
      const { data, error } = await supabase
        .from('external_feed_sync_runs')
        .select('id, started_at, finished_at, trigger_type, status, error')
        .eq('feed_id', feedId)
        .order('started_at', { ascending: false })
        .limit(10)
      if (error) throw error
      return (data || []) as SyncRun[]
    },
    enabled: !!feedId,
  })

  const { data: tvListingsDays } = useQuery({
    queryKey: [...APP_SETTINGS_QUERY_KEY, APP_SETTINGS_TV_LISTINGS_DAYS_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', APP_SETTINGS_TV_LISTINGS_DAYS_KEY)
        .maybeSingle()
      if (error || !data) return 7
      return data.value === '14' ? 14 : 7
    },
  })

  const { data: schedule } = useQuery({
    queryKey: ['admin-feed-schedule', feedId],
    queryFn: async () => {
      if (!feedId) return null
      const { data, error } = await supabase.from('admin_feed_schedule').select('*').eq('feed_id', feedId).maybeSingle()
      if (error) throw error
      return data as FeedSchedule | null
    },
    enabled: !!feedId,
  })

  useEffect(() => {
    if (schedule) {
      setScheduleEnabled(schedule.enabled)
      setScheduleWeekday(schedule.weekday)
      setScheduleTime(schedule.time_of_day)
      setScheduleTimezone(schedule.timezone)
    }
  }, [schedule])

  const saveDurationMutation = useMutation({
    mutationFn: async (days: 7 | 14) => {
      const { error } = await supabase.from('app_settings').upsert(
        { key: APP_SETTINGS_TV_LISTINGS_DAYS_KEY, value: String(days), updated_at: new Date().toISOString() },
        { onConflict: 'key' }
      )
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_SETTINGS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: TV_DATA_QUERY_KEY })
      setDurationMessage('Listing duration saved.')
      setTimeout(() => setDurationMessage(null), 5000)
    },
    onError: (err: Error) => {
      setDurationMessage(err.message)
      setTimeout(() => setDurationMessage(null), 8000)
    },
  })

  const clearExpiredMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('clear_expired_listing_data')
      if (error) throw error
      const row = Array.isArray(data) && data.length > 0 ? data[0] : data
      return {
        overlays: Number(row?.overlays_deleted ?? 0),
        tvSchedules: Number(row?.tv_schedules_deleted ?? 0),
        tvListings: Number(row?.tv_listings_deleted ?? 0),
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: TV_DATA_QUERY_KEY })
      const parts = []
      if (result.overlays > 0) parts.push(`${result.overlays} expired overlay(s)`)
      if (result.tvSchedules > 0) parts.push(`${result.tvSchedules} old schedule record(s)`)
      if (result.tvListings > 0) parts.push(`${result.tvListings} expired listing row(s)`)
      setClearExpiredMessage({
        type: 'success',
        text: parts.length > 0 ? `Cleared: ${parts.join('; ')}.` : 'No expired data to clear.',
      })
      setTimeout(() => setClearExpiredMessage(null), 6000)
    },
    onError: (err: Error) => {
      setClearExpiredMessage({ type: 'error', text: err.message })
      setTimeout(() => setClearExpiredMessage(null), 8000)
    },
  })

  const clearListingsMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('clear_tv_listings')
      if (error) throw error
      return Number(data ?? 0)
    },
    onSuccess: (deleted) => {
      queryClient.invalidateQueries({ queryKey: TV_DATA_QUERY_KEY })
      setClearListingsMessage({
        type: 'success',
        text: deleted > 0 ? `Cleared ${deleted} row(s) from TV listings.` : 'TV listings table is already empty.',
      })
      setTimeout(() => setClearListingsMessage(null), 6000)
    },
    onError: (err: Error) => {
      setClearListingsMessage({ type: 'error', text: err.message })
      setTimeout(() => setClearListingsMessage(null), 8000)
    },
  })

  const pruneEpgArchivesMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Not authenticated')

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      if (!supabaseUrl) throw new Error('Missing Supabase URL')

      const res = await fetch(`${supabaseUrl}/functions/v1/prune_epg_version_archives`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ keep_count: 1 }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(data?.error ?? `Prune failed (HTTP ${res.status})`)
      }
      return {
        deleted: Number(data?.deleted ?? 0),
        listed: Number(data?.listed ?? 0),
      }
    },
    onSuccess: (result) => {
      setPruneArchivesMessage({
        type: 'success',
        text:
          result.deleted > 0
            ? `Removed ${result.deleted} old EPG archive file(s) (found ${result.listed}). Kept the most recent in versions/freeview/.`
            : result.listed > 1
              ? `Found ${result.listed} archives but deleted 0 — run migration 120_list_epg_version_archive_paths.sql, then try again.`
              : 'No extra EPG archives to remove (already at most one).',
      })
      setTimeout(() => setPruneArchivesMessage(null), 8000)
    },
    onError: (err: Error) => {
      const hint = /failed to fetch|network/i.test(err.message)
        ? ' Deploy Edge Function prune_epg_version_archives.'
        : ''
      setPruneArchivesMessage({ type: 'error', text: `${err.message}${hint}` })
      setTimeout(() => setPruneArchivesMessage(null), 10000)
    },
  })

  const rebuildFlagsMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('rebuild_listings_accessibility_flags')
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TV_DATA_QUERY_KEY })
      setRebuildMessage({ type: 'success', text: 'TV accessibility flags rebuilt from overlays.' })
      setTimeout(() => setRebuildMessage(null), 6000)
    },
    onError: (err: Error) => {
      setRebuildMessage({ type: 'error', text: err.message })
      setTimeout(() => setRebuildMessage(null), 8000)
    },
  })

  const saveScheduleMutation = useMutation({
    mutationFn: async () => {
      if (!feedId) throw new Error('Missing feed')
      const nextRunAt = computeNextRunAt(scheduleWeekday, scheduleTime)
      const payload = {
        feed_id: feedId,
        enabled: scheduleEnabled,
        timezone: scheduleTimezone,
        weekday: scheduleWeekday,
        time_of_day: scheduleTime,
        next_run_at: nextRunAt,
        updated_by: userId,
      }
      if (schedule?.id) {
        const { error } = await supabase.from('admin_feed_schedule').update(payload).eq('id', schedule.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('admin_feed_schedule').insert(payload)
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-feed-schedule', feedId] })
    },
  })

  if (!feedId) {
    return (
      <AdminSectionCard id="epg" title="EPG & TV settings">
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Feed &quot;{FREEVIEW_EPG_FEED_NAME}&quot; not found. Run migration 019.
        </p>
      </AdminSectionCard>
    )
  }

  return (
    <div id="epg" className="space-y-6">
      <AdminSectionCard id="epg-status" title="EPG sync status">
        {syncState ? (
          <dl className="grid grid-cols-1 gap-2 text-sm">
            <div>
              <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Last success</dt>
              <dd style={{ color: 'var(--text-primary)' }}>
                {syncState.last_success_at ? format(new Date(syncState.last_success_at), 'PPp') : '—'}
              </dd>
            </div>
            <div>
              <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Last status</dt>
              <dd style={{ color: 'var(--text-primary)' }}>{syncState.last_status ?? '—'}</dd>
            </div>
            <div>
              <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Listings date range</dt>
              <dd style={{ color: 'var(--text-primary)' }}>
                {syncState.min_programme_date && syncState.max_programme_date
                  ? `${format(new Date(syncState.min_programme_date), 'd MMM yyyy')} – ${format(new Date(syncState.max_programme_date), 'd MMM yyyy')}`
                  : '— (run Refresh listings to detect)'}
              </dd>
            </div>
            {syncState.last_error && (
              <div>
                <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Last error</dt>
                <dd style={{ color: 'var(--color-error)' }}>{syncState.last_error}</dd>
              </div>
            )}
          </dl>
        ) : (
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No sync state yet.</p>
        )}
      </AdminSectionCard>

      <AdminSectionCard id="epg-schedule" title="Weekly EPG schedule">
        <p className="mb-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Automatic sync via scheduled edge function (independent of Refresh listings n8n batch).
        </p>
        <div className="flex flex-wrap gap-4 mb-4">
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
            <input type="checkbox" checked={scheduleEnabled} onChange={(e) => setScheduleEnabled(e.target.checked)} />
            Enabled
          </label>
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Weekday (UTC 0=Sun)
            <select
              value={scheduleWeekday}
              onChange={(e) => setScheduleWeekday(Number(e.target.value))}
              className="block mt-1 min-h-[44px] px-2 rounded border-2"
              style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)' }}
            >
              {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                <option key={d} value={d}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Time (UTC)
            <input
              type="time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              className="block mt-1 min-h-[44px] px-2 rounded border-2"
              style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)' }}
            />
          </label>
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Timezone label
            <input
              type="text"
              value={scheduleTimezone}
              onChange={(e) => setScheduleTimezone(e.target.value)}
              className="block mt-1 min-h-[44px] px-2 rounded border-2"
              style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)' }}
            />
          </label>
        </div>
        <button
          type="button"
          onClick={() => saveScheduleMutation.mutate()}
          disabled={saveScheduleMutation.isPending}
          className="px-4 py-2 font-semibold rounded text-sm"
          style={{ backgroundColor: 'var(--bg-interactive)', color: 'var(--text-inverse)' }}
        >
          {saveScheduleMutation.isPending ? 'Saving…' : 'Save schedule'}
        </button>
      </AdminSectionCard>

      <AdminSectionCard id="epg-duration" title="Listing duration">
        <p className="mb-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
          How many days of TV listings appear in the guide (7 or 14).
        </p>
        <div className="flex gap-2">
          {([7, 14] as const).map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => saveDurationMutation.mutate(days)}
              disabled={saveDurationMutation.isPending}
              className="px-4 py-2 rounded text-sm font-semibold border-2"
              style={{
                borderColor: tvListingsDays === days ? 'var(--color-focus)' : 'var(--bg-tertiary)',
                backgroundColor: tvListingsDays === days ? 'var(--bg-tertiary)' : 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
            >
              {days} days
            </button>
          ))}
        </div>
        {durationMessage && (
          <p className="mt-2 text-sm" style={{ color: 'var(--text-primary)' }} role="status">
            {durationMessage}
          </p>
        )}
      </AdminSectionCard>

      <AdminSectionCard id="epg-maintenance" title="TV listings maintenance">
        <p className="mb-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Clear expired overlays and old schedule metadata, or wipe all tv_listings rows before a full re-sync.
        </p>
        <p className="mb-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Each Refresh listings archives EPG XML under <code className="text-xs">versions/freeview/</code>. Future
          syncs keep only the latest archive automatically; use the button below to clear existing backlog.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          <button
            type="button"
            onClick={() => {
              if (
                !window.confirm(
                  'Delete all but the most recent EPG archive in Storage (versions/freeview/)? The current canonical epg.xml is not affected.'
                )
              ) {
                return
              }
              pruneEpgArchivesMutation.mutate()
            }}
            disabled={pruneEpgArchivesMutation.isPending}
            className="px-4 py-2 font-semibold rounded text-sm border"
            style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
          >
            {pruneEpgArchivesMutation.isPending ? 'Pruning…' : 'Prune old EPG archives'}
          </button>
        </div>
        {pruneArchivesMessage && (
          <p
            className="mb-3 text-sm"
            role="alert"
            style={{ color: pruneArchivesMessage.type === 'error' ? 'var(--color-error)' : 'var(--text-primary)' }}
          >
            {pruneArchivesMessage.text}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => clearExpiredMutation.mutate()}
            disabled={clearExpiredMutation.isPending}
            className="px-4 py-2 font-semibold rounded text-sm"
            style={{ backgroundColor: 'var(--bg-interactive)', color: 'var(--text-inverse)' }}
          >
            {clearExpiredMutation.isPending ? 'Clearing…' : 'Clear expired data'}
          </button>
          <button
            type="button"
            onClick={() => clearListingsMutation.mutate()}
            disabled={clearListingsMutation.isPending}
            className="px-4 py-2 font-semibold rounded text-sm border"
            style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
          >
            {clearListingsMutation.isPending ? 'Clearing…' : 'Clear all TV listings'}
          </button>
          <button
            type="button"
            onClick={() => rebuildFlagsMutation.mutate()}
            disabled={rebuildFlagsMutation.isPending}
            className="px-4 py-2 font-semibold rounded text-sm border"
            style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
          >
            {rebuildFlagsMutation.isPending ? 'Rebuilding…' : 'Re-sync overlays to TV listings'}
          </button>
        </div>
        {clearExpiredMessage && (
          <p className="mt-3 text-sm" role="alert" style={{ color: clearExpiredMessage.type === 'error' ? 'var(--color-error)' : 'var(--text-primary)' }}>
            {clearExpiredMessage.text}
          </p>
        )}
        {clearListingsMessage && (
          <p className="mt-3 text-sm" role="alert" style={{ color: clearListingsMessage.type === 'error' ? 'var(--color-error)' : 'var(--text-primary)' }}>
            {clearListingsMessage.text}
          </p>
        )}
        {rebuildMessage && (
          <p className="mt-3 text-sm" role="alert" style={{ color: rebuildMessage.type === 'error' ? 'var(--color-error)' : 'var(--text-primary)' }}>
            {rebuildMessage.text}
          </p>
        )}
      </AdminSectionCard>

      {runs.length > 0 && (
        <AdminSectionCard id="epg-runs" title="Recent EPG sync runs">
          <ul className="text-sm space-y-2 list-none p-0 m-0">
            {runs.map((run) => (
              <li key={run.id} style={{ color: 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--text-primary)' }}>{run.status}</span>
                {' · '}
                {format(new Date(run.started_at), 'dd MMM yyyy HH:mm')}
                {' · '}
                {run.trigger_type}
                {run.error && <span style={{ color: 'var(--color-error)' }}> — {run.error}</span>}
              </li>
            ))}
          </ul>
        </AdminSectionCard>
      )}
    </div>
  )
}
